import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import updateRouteQuery from '@/utils/updateRouteQuery';
import { validateDateRange, validateQuery } from '@/utils/validateSearchQuery';
import { useDestination as v2UseDestination } from '@/v2/contexts/destination';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { dataLayer } from '@/v2/utils/dataLayer';
import useDestination from '../../../../hooks/useDestination';
import useOnSearch from '../../../../hooks/useOnSearch';
import BFF from '../../../../services/BFF';
import getSearchQuery from '../../../../utils/getSearchQuery';
import usePropertiesContent from '../usePropertiesContent';
import useViewSearchResultsDataLayerEvent from '../useViewSearchResultsDataLayerEvent';
import GetPackagesCheapestOfferContext from './GetPackagesCheapestOfferContext';

/**
 * @param {{ children: React.ReactNode }} props
 */
const GetPackagesCheapestOfferProvider = ({ children }) => {
  const shouldUseNewEvents = useGA4Events();

  const d = useDestination();
  const dV2 = v2UseDestination();
  const destination = d || dV2;

  const handleViewSearchResultsDataLayerEvent =
    useViewSearchResultsDataLayerEvent();

  const propertiesContent = usePropertiesContent();

  const [state, setState] = useState({});
  const [searchQuery, setSearchQuery] = useState();
  const [paramError, setParamError] = useState(undefined);

  const getPackagesCheapestOffer = useCallback(
    async (searchParams) => {
      setState({});
      setParamError(undefined);

      if (!searchParams) {
        setSearchQuery();
        return;
      }

      if (!validateQuery(destination.searchConfig, searchParams)) {
        setSearchQuery();
        updateRouteQuery(getSearchQuery({}), true);

        if (!validateDateRange(null, searchParams)) {
          setParamError('invalid search dates');
        }
        return;
      }

      const propertyIdsByDestinationCode = propertiesContent.reduce(
        (acc, cur) => {
          const { destinationCode } = cur.destination.searchConfig;

          return {
            ...acc,
            [destinationCode]: [...(acc[destinationCode] || []), cur.id],
          };
        },
        {},
      );

      const params = {
        ...searchParams,
        ...propertyIdsByDestinationCode,
        destinationCodes: Object.keys(propertyIdsByDestinationCode),
      };

      setSearchQuery(getSearchQuery(params));
      const data = await BFF.getPackagesCheapestOffer()(params);

      setState(data);
      handleViewSearchResultsDataLayerEvent(data, params);
    },
    [
      destination.searchConfig,
      handleViewSearchResultsDataLayerEvent,
      propertiesContent,
    ],
  );

  const {
    params,
    error: apiError,
    loading,
  } = useOnSearch(getPackagesCheapestOffer);

  useEffect(() => {
    if (shouldUseNewEvents && apiError?.code) {
      dataLayer.searchErrorEvent({
        id: apiError?.code,
        message: apiError?.message,
        searchTerm: destination?.title,
      });
    }
  }, [
    destination?.title,
    apiError?.code,
    apiError?.message,
    shouldUseNewEvents,
  ]);

  const { packageOffers, availability, flightOptions } = state;
  const value = useMemo(
    () => ({
      params,
      error: apiError || paramError,
      loading,
      packagesWithCheapestOffer: packageOffers,
      availability,
      searchQuery,
      flightOptions,
    }),
    [
      apiError,
      availability,
      flightOptions,
      loading,
      packageOffers,
      paramError,
      params,
      searchQuery,
    ],
  );

  return (
    <GetPackagesCheapestOfferContext.Provider value={value}>
      {children}
    </GetPackagesCheapestOfferContext.Provider>
  );
};

GetPackagesCheapestOfferProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default GetPackagesCheapestOfferProvider;
